import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';

export default function HeroSection() {
  const videoRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (videoRef.current) videoRef.current.play();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative bg-[#0A0F1C] overflow-hidden"
      style={{ minHeight: '100vh' }}
    >
      {/* Fullscreen video background */}
      <video
        ref={videoRef}
        muted loop playsInline
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      >
        <source src="/hero-video.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1C]/95 via-[#0A0F1C]/70 to-[#0A0F1C]/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1C]/50 via-transparent to-[#0A0F1C]/90" />

      <div className="max-w-content mx-auto px-6 flex items-center min-h-screen pt-36 lg:pt-32 pb-16 relative z-10">
        <div className="w-full max-w-2xl z-10">
          <motion.h1
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="text-white mb-7 font-display font-black tracking-tighter leading-[0.95]"
            style={{ fontSize: 'clamp(3rem, 2rem + 4vw, 5.5rem)' }}
          >
            We Build It.<br />We Protect It.
          </motion.h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="text-[#A0AEC0] text-base lg:text-lg max-w-xl mb-10 leading-relaxed"
          >
            Most technology partners do development or security. Flynaut does both, together, so nothing falls through the cracks. AI, applications, cloud, cybersecurity, and modernization under one roof.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.65 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="/assessment"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-4 text-base font-medium transition-all duration-200 shadow-[0_0_24px_rgba(245,132,31,0.25)] hover:shadow-[0_0_32px_rgba(245,132,31,0.4)]"
            >
              Start Your Transformation <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center justify-center gap-2.5 border border-orange-500/60 text-orange-500 hover:text-orange-400 hover:border-orange-500 hover:bg-orange-500/5 rounded-full px-8 py-4 text-base font-medium transition-all duration-200"
            >
              Explore Our Capabilities <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-chevron" aria-hidden="true">
        <ChevronDown size={24} className="text-white/30" />
      </div>
    </section>
  );
}
